import { Trans, t } from '@lingui/macro'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, styled } from '@mui/material'
import { Box } from '@mui/system'
import PhenotypeIcon from '@om1/cohort-module/src/assets/phenotype-icon.png'
import { ExplorerCohortsService, OpenAPI } from '@om1/falcon-api'
import { useUserPermissions } from '@om1/platform-authentication'
import { BasicCard, BasicCardHeader, BasicCardStat } from '@om1/platform-components/BasicCard'
import { LightTooltip } from '@om1/platform-components/Tooltip'
import { Routes, toPath } from '@om1/platform-routing'
import { TrackingActionTypes } from '@om1/platform-tracking'
import { ActionWithPayload, PlatformPermissions } from '@om1/platform-utils'
import axios from 'axios'
import { get } from 'lodash'
import { useSnackbar } from 'notistack'
import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { hasPhenotypeBlock } from '../../components/edit/utils/filter-import'
import { CohortSizeWithLoader } from '../../components/shared/CohortSizeWithLoader'
import { CohortRoutes } from '../../routes'
import { CohortListItem, ExceptFilterOperator, ReportAnalyticsType } from '../../state'
import { DashboardCohortOwner } from './CohortDashboardPageComponent'

interface PngIconProps {
    height: string
    selected: boolean
}

const PngIcon = styled('img')<PngIconProps>`
    height: ${(props) => props.height};
    filter: ${(props) => (props.selected ? 'none' : 'invert(1)')};
    border-radius: ${(props) => props.height};
`

export interface DashboardCohortCardProps {
    cohort: CohortListItem
    isSelected: boolean
    sizeLoading: boolean
    onDeleteView: (id: string) => void
    onDuplicate: (id: string, shouldUnmarkFeatured?: boolean) => void
    reportType: ReportAnalyticsType
    onRefreshSize: (id: string, name: string) => void
    onUpdateCohort?: (updatedCohort: CohortListItem) => void
    setCohortId: React.Dispatch<React.SetStateAction<string | undefined>>
    trackPage: (route: string) => ActionWithPayload<
        TrackingActionTypes.TRACK_PAGE,
        {
            location: {
                pathname: string
                search: string
                hash: string
                key: string
            }
            action: string
            isFirstRendering: boolean
        }
    >
}

export const DashboardCohortCard = ({
    cohort,
    isSelected,
    sizeLoading,
    onDeleteView,
    onDuplicate,
    onRefreshSize,
    onUpdateCohort,
    reportType,
    setCohortId,
    trackPage
}: DashboardCohortCardProps) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null)
    const [toggleFeaturedLoading, setToggleFeaturedLoading] = useState(false)
    const menuOpen = Boolean(menuAnchor)
    const location = useLocation()
    const { enqueueSnackbar } = useSnackbar()

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        event.preventDefault()
        setMenuAnchor(event.currentTarget)
    }
    const handleMenuClose = (event) => setMenuAnchor(null)
    const { permissions } = useUserPermissions()

    const handleToggleFeatured = useCallback(async () => {
        if (toggleFeaturedLoading) return

        // Ensure we have a proper boolean value for the API call
        const currentFeaturedStatus = Boolean(cohort.isFeatured)
        const newFeaturedStatus = !currentFeaturedStatus

        setToggleFeaturedLoading(true)
        try {
            // Explicitly ensure the boolean value is properly serialized
            const requestBody = newFeaturedStatus === true ? true : false

            let updatedCohort

            if (requestBody === false) {
                // WORKAROUND: The OpenAPI client has a bug where it converts false to undefined
                // So we make a direct axios call for the false case

                const url = `${OpenAPI.BASE}/explorer/cohorts/${cohort.id}/feature`

                // Build headers with proper async token resolution
                const headers: Record<string, string> = {
                    'Content-Type': 'application/json'
                }

                // Create proper ApiRequestOptions for token/header resolution
                const requestOptions = {
                    method: 'PATCH' as const,
                    url: `/explorer/cohorts/${cohort.id}/feature`,
                    body: false
                }

                if (OpenAPI.TOKEN) {
                    const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : await OpenAPI.TOKEN(requestOptions)
                    // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                    headers['Authorization'] = `Bearer ${token}`
                }

                if (OpenAPI.HEADERS) {
                    const additionalHeaders = typeof OpenAPI.HEADERS === 'function' ? await OpenAPI.HEADERS(requestOptions) : OpenAPI.HEADERS
                    Object.assign(headers, additionalHeaders)
                }

                const response = await axios.patch(url, false, {
                    headers,
                    withCredentials: OpenAPI.WITH_CREDENTIALS
                })
                updatedCohort = response.data
            } else {
                // Use the normal OpenAPI client for true values (which work correctly)
                updatedCohort = await ExplorerCohortsService.toggleFeaturedStatusExplorerCohortsCohortIdFeaturePatch({
                    cohortId: cohort.id,
                    requestBody: requestBody
                })
            }

            // Update the cohort in the parent component's state
            if (onUpdateCohort && updatedCohort) {
                onUpdateCohort(updatedCohort as CohortListItem)
            }

            enqueueSnackbar(currentFeaturedStatus ? t`Cohort unmarked as featured successfully` : t`Cohort marked as featured successfully`, {
                variant: 'success',
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            })
        } catch (error: any) {
            enqueueSnackbar(currentFeaturedStatus ? t`Failed to unmark cohort as featured` : t`Failed to mark cohort as featured`, {
                variant: 'error',
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            })
        } finally {
            setToggleFeaturedLoading(false)
        }
    }, [cohort.id, cohort.isFeatured, onUpdateCohort, toggleFeaturedLoading, enqueueSnackbar])

    const menu = useMemo(() => {
        let actions: ReactNode[] = []
        const isFeatured = Boolean(cohort.isFeatured)

        // Hide Edit option for featured cohorts
        if (!cohort.isSystem && !isFeatured && permissions?.includes(PlatformPermissions.UPDATE_COHORT)) {
            actions.push(
                <MenuItem
                    data-testid={'edit-cohort'}
                    key='edit'
                    color='primary'
                    component={Link}
                    to={toPath(Routes.COHORTBUILD) + `/${DashboardCohortOwner.CUSTOM}/${cohort.id}/edit`}
                    aria-label={t`Edit`}
                >
                    <ListItemIcon>
                        <EditOutlinedIcon fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText>
                        <Trans>Edit Cohort</Trans>
                    </ListItemText>
                </MenuItem>
            )
        }
        if (!cohort.isSystem && !isFeatured) {
            actions.push(
                <MenuItem
                    key='refreshCount'
                    color='primary'
                    aria-label={t`Refresh Count`}
                    disabled={sizeLoading}
                    onClick={() => onRefreshSize(cohort.id, cohort.name)}
                >
                    <ListItemIcon>
                        <RefreshOutlinedIcon fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText>
                        <Trans>Refresh Count</Trans>
                    </ListItemText>
                </MenuItem>
            )
        }
        if (permissions?.includes(PlatformPermissions.ACCESS_DELIVERY)) {
            actions.push(
                <MenuItem
                    key='toggleFeatured'
                    color='primary'
                    aria-label={isFeatured ? t`Unmark as Featured` : t`Mark as Featured`}
                    disabled={toggleFeaturedLoading}
                    onClick={handleToggleFeatured}
                >
                    <ListItemIcon>
                        {isFeatured ? <StarIcon fontSize='small' color='primary' /> : <StarBorderIcon fontSize='small' color='primary' />}
                    </ListItemIcon>
                    <ListItemText>{isFeatured ? <Trans>Unmark as Featured</Trans> : <Trans>Mark as Featured</Trans>}</ListItemText>
                </MenuItem>
            )
        }
        if (permissions?.includes(PlatformPermissions.CREATE_COHORT)) {
            // For featured cohorts, pass a flag to unmark the duplicate as featured
            const handleDuplicateClick = () => onDuplicate(cohort.id, isFeatured)

            actions.push(
                <MenuItem key='duplicate' color='primary' aria-label={t`Duplicate`} onClick={handleDuplicateClick}>
                    <ListItemIcon>
                        <ContentCopyIcon fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText>
                        <Trans>Duplicate</Trans>
                    </ListItemText>
                </MenuItem>
            )
        }
        // Hide Delete option for featured cohorts
        if (!isFeatured && permissions?.includes(PlatformPermissions.DELETE_COHORT)) {
            actions.push(
                <MenuItem key='delete' color='primary' aria-label={t`Delete`} onClick={() => onDeleteView(cohort.id)}>
                    <ListItemIcon>
                        <DeleteOutlinedIcon fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText>
                        <Trans>Delete</Trans>
                    </ListItemText>
                </MenuItem>
            )
        }

        if (actions.length && !cohort.isSystem) {
            return (
                <>
                    <IconButton
                        id={`dashboard-actions-toggle-${cohort.id}`}
                        onClick={handleMenuOpen}
                        aria-label={t`Cohort Actions`}
                        aria-controls={menuOpen ? `dashboard-actions-${cohort.id}` : undefined}
                        aria-haspopup='true'
                        aria-expanded={menuOpen ? 'true' : undefined}
                        style={isSelected ? { color: 'white' } : { color: '#002D72' }}
                        sx={{ position: 'absolute', bottom: 0, right: 0, padding: 1 }}
                    >
                        <MoreHorizIcon />
                    </IconButton>
                    <Menu
                        id={`dashboard-actions-${cohort.id}`}
                        open={menuOpen}
                        anchorEl={menuAnchor}
                        onClose={handleMenuClose}
                        onClick={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        MenuListProps={{ 'aria-labelledby': `dashboard-actions-toggle-${cohort.id}` }}
                    >
                        {actions}
                    </Menu>
                </>
            )
        } else {
            return null
        }
    }, [
        cohort,
        isSelected,
        sizeLoading,
        onDeleteView,
        onDuplicate,
        onRefreshSize,
        menuAnchor,
        menuOpen,
        permissions,
        toggleFeaturedLoading,
        handleToggleFeatured
    ])

    const labelProps = useMemo(() => ({ color: isSelected ? 'secondary.main' : undefined }), [isSelected])

    return (
        <BasicCard isSelected={isSelected}>
            <BasicCardHeader label={<CohortListItemToTitle cohort={cohort} isSelected={isSelected} />} />
            <Box display='flex' flexDirection='column' gap={1}>
                <BasicCardStat
                    id='cohort-card-size'
                    label={<Trans>Cohort Size</Trans>}
                    labelProps={labelProps}
                    value={
                        <CohortSizeWithLoader
                            cohortSize={cohort.cohortSize}
                            datasetSize={cohort.analyticsDataset.size}
                            isStale={cohort.isStale}
                            sizeLoading={sizeLoading}
                            isSystem={cohort.isSystem}
                        />
                    }
                />
                {!cohort.isSystem && !cohort.isFeatured && (
                    <BasicCardStat id='cohort-card-owner' label={<Trans>Owner</Trans>} value={cohort.createdByEmail} labelProps={labelProps} />
                )}
            </Box>
            <Box
                style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}
                onClick={() => {
                    setCohortId(cohort.id)
                    const route = `/${CohortRoutes.COHORTBUILD}/${CohortRoutes.DASHBOARD}/${
                        cohort.isSystem ? DashboardCohortOwner.DATASET : DashboardCohortOwner.CUSTOM
                    }/${cohort.id}/${reportType}${location.search}`
                    trackPage(route)
                    window.history.pushState({}, '', route)
                }}
                sx={{ cursor: 'pointer' }}
            />
            {menu}
        </BasicCard>
    )
}

export const CohortListItemToTitle: React.FunctionComponent<{ cohort: CohortListItem; isSelected?: boolean }> = ({
    cohort,
    isSelected
}: {
    cohort: CohortListItem
    isSelected?: boolean
}) => {
    const hasPhenotype = useMemo(() => {
        const queryFilters: boolean[] = get(cohort, ['query', 'filters'], []).map(
            (filter: ExceptFilterOperator) => hasPhenotypeBlock(filter) ?? false
        )

        return queryFilters.some((f) => f)
    }, [cohort])

    const cardHeaderLabel = useMemo(() => {
        if (!hasPhenotype) {
            return <>{cohort.name}</>
        }

        return (
            <Box display='flex' flexDirection='row' flexWrap='nowrap' justifyContent='space-between' alignItems='center'>
                <span>{cohort.name}</span>
                <LightTooltip key={'editTooltip'} title={<Trans>Powered by PhenOM</Trans>} placement='top' sx={{ zIndex: 10000 }}>
                    <PngIcon src={PhenotypeIcon} height='28px' selected={isSelected ? isSelected : false} sx={{ marginLeft: '18px' }} />
                </LightTooltip>
            </Box>
        )
    }, [cohort.name, hasPhenotype, isSelected])

    return cardHeaderLabel
}
